import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Banknote, Building2, BriefcaseBusiness, CalendarCheck,
  CarFront, ChevronDown, CircleDollarSign, Coins, GraduationCap, HeartHandshake,
  House, KeyRound, Landmark, Lightbulb, LockKeyhole, Mail, PiggyBank, RefreshCw, Repeat,
  ShieldCheck, Target, Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Section, Eyebrow, Heading, VideoBlock, GatedForm } from "@/components/site/pageui";
import { LeadForm } from "@/components/site/LeadForm";
import policyStackImg from "@/assets/policy-stack-dashboard.png";
import "./infinite-banking.css";

/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */
const ease = [0.22, 0.61, 0.36, 1];
const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease } }),
};

function Reveal({ children, i = 0, className = "", as = "div" }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      variants={fade}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      {children}
    </MotionTag>
  );
}

const IbIcon = ({ icon: I, size = "", tone = "" }) => (
  <span className={`ibv2-icon ${size} ${tone}`.trim()}>
    <I size={size === "sm" ? 18 : size === "lg" ? 26 : 22} strokeWidth={1.5} />
  </span>
);

const goldDefs = (id = "ibv2-gold") => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#e6cf98" />
      <stop offset="50%" stopColor="#C7A15A" />
      <stop offset="100%" stopColor="#b3893f" />
    </linearGradient>
  </defs>
);

/* -------------------------------------------------------------------------- */
/* Accordion card                                                             */
/* -------------------------------------------------------------------------- */
function AccordionCard({ title, icon, children, i = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal i={i} className="h-full">
      <article className={`ibv2-acc ${open ? "open" : ""}`}>
        <button
          type="button"
          className="ibv2-acc__trigger focus-ring"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="flex items-center justify-between gap-4">
            <IbIcon icon={icon} />
            <ChevronDown size={20} className={`ibv2-chev text-bronze transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </span>
          <h3 className="ibv2-acc__title mt-5">{title}</h3>
        </button>
        <div className={`ibv2-panel ${open ? "open" : ""}`}>
          <div>
            <p className="ibv2-acc__body px-[1.4rem] pb-6 text-sm leading-relaxed">{children}</p>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

/* -------------------------------------------------------------------------- */
/* 0. HERO                                                                    */
/* -------------------------------------------------------------------------- */
export function InfiniteBankingHero() {
  const steps = [
    [Wallet, "Income"],
    [Landmark, "Family Banking"],
    [Lightbulb, "Opportunity"],
    [CarFront, "Purchase"],
    [RefreshCw, "Reuse"],
  ];
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#FBF8F1] pb-20 pt-[120px] md:pb-28 md:pt-[150px]">
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.22),transparent_70%)]" />
      <div className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.12),transparent_70%)]" />
      <div className="container-lr relative grid items-center gap-12 lg:grid-cols-[1.02fr_.98fr]">
        <Reveal>
          <Eyebrow>Family Banking · Infinite Banking · Liquidity · Control · Legacy</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-semibold leading-[.97] tracking-display text-legacy sm:text-6xl lg:text-7xl">
            You&apos;re already financing your life.<br />
            <span className="accent-word">The question is who benefits.</span>
          </h1>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-slate md:text-lg">
            Every family already has a financial system. Infinite Banking is a process for organizing cash flow, capital, financing and repayment with greater intention.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <Link to="/resources#save-spend-overview" data-testid="hero-explainer-cta" className="btn-base btn-primary"><CircleDollarSign size={18} />Watch the Overview</Link>
            <Link to="/strategy-call" className="btn-base btn-outline-dark">Book Your Free Strategy Call <ArrowRight size={18} /></Link>
          </div>
        </Reveal>

        <Reveal i={1}>
          <div className="relative overflow-hidden rounded-[22px] border border-gold/45 bg-white/90 p-7 shadow-[0_28px_60px_-40px_rgba(17,17,17,0.5)] backdrop-blur">
            <div className="pointer-events-none absolute inset-6 rounded-full border border-gold/15" />
            <p className="eyebrow text-center">An intentional capital cycle</p>
            <div className="relative mt-6 flex flex-col items-center">
              {steps.map(([icon, label], idx) => (
                <div key={label} className="contents">
                  <div className={`relative z-[1] flex min-w-[13rem] items-center gap-3 rounded-full border px-4 py-2.5 font-serif text-xl ${idx === 1 ? "border-gold bg-[#1a1714] text-ivory shadow-[0_0_26px_rgba(199,161,90,0.3)]" : "border-gold/40 bg-white text-legacy shadow-[0_12px_24px_-20px_rgba(17,17,17,0.7)]"}`}>
                    <IbIcon icon={icon} size="sm" tone={idx === 1 ? "dark" : ""} />
                    <span>{label}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <span aria-hidden="true" className="my-1 h-6 w-px bg-gradient-to-b from-gold/20 via-gold to-gold/20" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 1. HIDDEN FINANCIAL PROBLEM                                                */
/* -------------------------------------------------------------------------- */
export function HiddenFinancialProblem() {
  const cards = [
    [Wallet, "Money leaves without intention", "Without a clear system, recurring payments can become automatic instead of deliberate."],
    [Coins, "Financing quietly changes long-term wealth", "Interest, timing, and missed liquidity all influence the true cost of a purchase."],
    [Lightbulb, "Capital may not be ready for opportunity", "A healthy system considers how capital can be accessed before it is actually needed."],
  ];
  return (
    <Section id="plain-language" className="bg-ivory">
      <Reveal className="max-w-3xl">
        <Eyebrow>The Hidden Financial Problem</Eyebrow>
        <Heading>Most families don&apos;t have an income problem.<br /><span className="accent-word">They have a system problem.</span></Heading>
      </Reveal>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {cards.map(([icon, title, copy], i) => (
          <AccordionCard key={title} title={title} icon={icon} i={i}>{copy}</AccordionCard>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. EVERY FAMILY ALREADY HAS A BANKING SYSTEM (comparison)                  */
/* -------------------------------------------------------------------------- */
function ComparisonPath({ intentional }) {
  const nodes = intentional
    ? ["Income", "Organized capital", "Directed use", "Repayment", "Reuse"]
    : ["Income", "Outgoing payments", "Interest leaves", "Restart"];
  const stroke = intentional ? "url(#ibv2-gold)" : "#8f8a83";
  return (
    <article className={`rounded-[20px] border p-6 md:p-8 transition-all duration-500 ${intentional ? "border-gold/70 bg-[linear-gradient(165deg,#fff,#fbf2df)] shadow-[0_26px_55px_-34px_rgba(17,17,17,0.45)]" : "border-taupe/50 bg-white"}`}>
      <div className="flex items-center gap-3">
        <IbIcon icon={intentional ? Landmark : Building2} tone={intentional ? "dark" : ""} />
        <div>
          <p className="eyebrow">{intentional ? "The intentional path" : "The default path"}</p>
          <h3 className="mt-1 font-serif text-2xl text-legacy md:text-3xl">{intentional ? "Family Banking System" : "Traditional Banking System"}</h3>
        </div>
      </div>
      <div className="mt-7 flex flex-wrap items-center gap-x-2 gap-y-3">
        {nodes.map((n, i) => (
          <div key={n} className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-3 py-1.5 text-[0.82rem] font-medium ${intentional ? "border-gold/60 bg-white text-legacy" : "border-taupe/60 bg-[#f4f2ee] text-slate"}`}>{n}</span>
            {i < nodes.length - 1 && (
              <svg width="26" height="12" viewBox="0 0 26 12" className="shrink-0" aria-hidden="true">
                {goldDefs()}
                <line x1="0" y1="6" x2="20" y2="6" stroke={stroke} strokeWidth="2" className={intentional ? "ibv2-flow-dash" : ""} />
                <path d="M18 2 L24 6 L18 10" fill="none" stroke={stroke} strokeWidth="2" />
              </svg>
            )}
          </div>
        ))}
      </div>
      <p className={`mt-6 flex items-center gap-2 text-sm font-medium ${intentional ? "text-bronze" : "text-slate"}`}>
        {intentional ? <><RefreshCw size={16} /> Capital returns and is used again.</> : <><ArrowRight size={16} /> Capital leaves the family system.</>}
      </p>
    </article>
  );
}

export function BankingSystemComparison() {
  return (
    <Section id="banking-function" className="bg-[#EFE8D9]">
      <Reveal className="mx-auto max-w-4xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow">Every Family Already Has A Banking System</p><span className="gold-rule" /></div>
        <h2 className="section-heading mt-6 text-legacy">The choice is not whether money moves. It&apos;s whether the movement is <span className="accent-word">intentional.</span></h2>
      </Reveal>
      <div className="mt-12 grid items-stretch gap-5 lg:grid-cols-[1fr_auto_1fr]">
        <Reveal i={0}><ComparisonPath /></Reveal>
        <div className="hidden items-center justify-center lg:flex">
          <span className="rounded-full border border-gold/50 bg-ivory px-4 py-3 font-serif text-xl text-bronze">vs.</span>
        </div>
        <Reveal i={1}><ComparisonPath intentional /></Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. WHAT BANKING ACTUALLY MEANS (circular 4-node wheel)                     */
/* -------------------------------------------------------------------------- */
export function BankingFunctionDiagram() {
  const items = [
    [PiggyBank, "Store Capital", "Set capital aside in a place built to hold it.", { top: "4%", left: "50%" }],
    [Wallet, "Access Capital", "Reach that capital when you actually need it.", { top: "50%", left: "96%" }],
    [CircleDollarSign, "Direct Capital", "Point it toward priorities and opportunities.", { top: "96%", left: "50%" }],
    [RefreshCw, "Replenish Capital", "Rebuild it so the system can work again.", { top: "50%", left: "4%" }],
  ];
  const [active, setActive] = useState(null);
  return (
    <Section id="how-it-works" className="bg-[#FBF8F1]">
      <div className="grid items-center gap-12 lg:grid-cols-[.82fr_1.18fr]">
        <Reveal>
          <Eyebrow>What Banking Actually Means</Eyebrow>
          <Heading>Understand the function before choosing the tool.</Heading>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate">
            Banking is the repeatable work of storing capital, accessing it, directing it toward priorities, and replenishing it after use.
          </p>
          <ul className="mt-6 hidden gap-2 lg:grid">
            {items.map(([icon, label, desc], idx) => (
              <li key={label}
                onMouseEnter={() => setActive(idx)}
                onMouseLeave={() => setActive(null)}
                className={`flex items-start gap-3 rounded-xl border p-3 transition-all duration-300 ${active === idx ? "border-gold/70 bg-white shadow-[0_16px_34px_-26px_rgba(17,17,17,0.5)]" : "border-transparent"}`}>
                <IbIcon icon={icon} size="sm" tone={active === idx ? "dark" : ""} />
                <div><p className="font-serif text-lg text-legacy">{label}</p><p className="text-sm text-slate">{desc}</p></div>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal i={1}>
          {/* Circular diagram (md+) */}
          <div className={`ibv2-wheel hidden md:block ${active !== null ? "active" : ""}`}>
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden="true">
              {goldDefs()}
              <circle cx="50" cy="50" r="38" fill="none" className="ibv2-orbit-dash" />
              <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(199,161,90,0.18)" strokeWidth="0.6" />
            </svg>
            <div className="ibv2-wheel__center">
              <Landmark size={26} className="text-gold" />
              <span className="px-2 font-serif text-[1.05rem] leading-tight">
                {active === null ? "The Banking Function" : items[active][1]}
              </span>
            </div>
            {items.map(([icon, label, , pos], idx) => (
              <div key={label} className={`ibv2-wheel__node ${active === idx ? "on" : ""}`} style={pos}
                onMouseEnter={() => setActive(idx)} onMouseLeave={() => setActive(null)}
                tabIndex={0} onFocus={() => setActive(idx)} onBlur={() => setActive(null)}>
                <IbIcon icon={icon} />
                <span>{label}</span>
              </div>
            ))}
          </div>
          {/* Stacked (mobile) */}
          <div className="grid gap-3 md:hidden">
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-gold/70 bg-[#1a1714] p-5 text-center text-ivory">
              <Landmark size={24} className="text-gold" /><span className="font-serif text-xl">The Banking Function</span>
            </div>
            {items.map(([icon, label, desc]) => (
              <div key={label} className="ibv2-card flex items-center gap-3 p-4">
                <IbIcon icon={icon} size="sm" /><div><p className="font-serif text-lg text-legacy">{label}</p><p className="text-sm text-slate">{desc}</p></div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 4. MONEY FLOWS (Traditional + Family Banking)                             */
/* -------------------------------------------------------------------------- */
const debtNodes = [
  [House, "Mortgage"], [CarFront, "Vehicle Loan"], [CircleDollarSign, "Credit Cards"],
  [GraduationCap, "Student Loans"], [ShieldCheck, "Insurance Premiums"],
];

function TraditionalFlow() {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-gold/40 bg-[linear-gradient(180deg,#fff,#f5efe4)] p-6 shadow-[0_22px_50px_-38px_rgba(17,17,17,0.5)] md:p-9">
      {/* Income */}
      <div className="ibv2-node-hub mx-auto flex w-max max-w-full items-center gap-3 rounded-2xl border border-gold/55 bg-white px-5 py-3.5 shadow-[0_14px_28px_-24px_rgba(17,17,17,0.6)]">
        <IbIcon icon={Wallet} /><div><p className="eyebrow">Your income</p><p className="font-serif text-2xl text-legacy">Your paycheck</p></div>
      </div>
      {/* fan-out streams: paycheck -> each category */}
      <svg viewBox="0 0 1000 90" preserveAspectRatio="none" className="mx-auto -mb-2 mt-1 h-16 w-full" aria-hidden="true">
        {goldDefs()}
        {[100, 300, 500, 700, 900].map((x) => (
          <path key={x} d={`M500 0 C500 48 ${x} 40 ${x} 90`} fill="none" stroke="url(#ibv2-gold)" strokeWidth="2.8" className="ibv2-flow-dash" />
        ))}
      </svg>
      {/* destinations — each card carries a gold "through" connector top & bottom */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {debtNodes.map(([icon, label], i) => (
          <div key={label} className={`ibv2-node ibv2-node--flow ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}>
            <IbIcon icon={icon} size="sm" /><span>{label}</span>
          </div>
        ))}
      </div>
      {/* converge streams: each category -> Banks */}
      <svg viewBox="0 0 1000 96" preserveAspectRatio="none" className="mx-auto -mt-2 mb-1 h-16 w-full" aria-hidden="true">
        {goldDefs()}
        {[100, 300, 500, 700, 900].map((x) => (
          <path key={x} d={`M${x} 0 C${x} 50 500 46 500 92`} fill="none" stroke="url(#ibv2-gold)" strokeWidth="2.8" className="ibv2-flow-dash" />
        ))}
        <path d="M491 82 L500 96 L509 82" fill="none" stroke="url(#ibv2-gold)" strokeWidth="2.8" />
      </svg>
      {/* bank */}
      <div className="mx-auto flex w-max max-w-full items-center gap-3 rounded-2xl border border-legacy/25 bg-[#1a1714] px-5 py-3.5 text-ivory shadow-[0_18px_36px_-26px_rgba(17,17,17,0.7)]">
        <IbIcon icon={Building2} tone="dark" /><div><p className="font-serif text-2xl">Banks &amp; Finance Companies</p><p className="text-sm text-taupe">They keep the interest. Capital leaves your system.</p></div>
      </div>
    </div>
  );
}

/* (Family Banking Flow removed per V2 final refinement — Capital Cycle now carries the family-banking narrative.) */

export function MoneyFlows() {
  return (
    <Section id="money-flow" className="bg-ivory">
      <Reveal className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow">Traditional Money Flow</p><span className="gold-rule" /></div>
        <Heading>See where the capital stream ends.</Heading>
      </Reveal>
      <Reveal i={1} className="mt-12"><TraditionalFlow /></Reveal>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 5. CAPITAL CYCLE (charcoal flywheel)                                       */
/* -------------------------------------------------------------------------- */
export function CapitalCycleDiagram() {
  const stages = [
    [Landmark, "Family Banking System"],
    [KeyRound, "Access Capital"],
    [Target, "Deploy Capital"],
    [RefreshCw, "Repay Capital"],
    [PiggyBank, "Rebuild Liquidity"],
  ];
  const [active, setActive] = useState(null);
  const R = 40; // radius %
  return (
    <Section id="capital-cycle" className="relative overflow-hidden bg-legacy">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[60%] w-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.16),transparent_70%)]" />
      <Reveal className="relative mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow text-gold">The Capital Cycle</p><span className="gold-rule" /></div>
        <h2 className="section-heading mt-6 text-ivory">A system built to be used, replenished, and <span className="accent-word">used again.</span></h2>
      </Reveal>

      <Reveal i={1} className="relative mt-12">
        {/* Income entry point + connector into Family Banking System (md+) */}
        <div className="hidden flex-col items-center md:flex">
          <div className="flex flex-col items-center gap-2">
            <IbIcon icon={Wallet} tone="dark" />
            <span className="font-serif text-lg text-ivory">Income</span>
          </div>
          <svg width="16" height="48" viewBox="0 0 16 48" className="mt-1" aria-hidden="true">
            {goldDefs()}
            <line x1="8" y1="0" x2="8" y2="38" stroke="url(#ibv2-gold)" strokeWidth="2.6" className="ibv2-flow-dash" />
            <path d="M3 36 L8 46 L13 36" fill="none" stroke="url(#ibv2-gold)" strokeWidth="2.6" />
          </svg>
        </div>
        {/* Flywheel (md+) — 5 functional stages */}
        <div className="ibv2-fly -mt-1 hidden md:block">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden="true">
            {goldDefs()}
            <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(199,161,90,0.25)" strokeWidth="1" />
            <circle cx="50" cy="50" r={R} fill="none" className="ibv2-orbit-dash" />
          </svg>
          <div className="ibv2-fly__center">
            <Repeat size={22} className="text-gold" />
            <span className="px-2 font-serif text-lg leading-tight tracking-wide">CAPITAL CYCLE</span>
          </div>
          {stages.map(([icon, label], idx) => {
            const angle = (-90 + idx * 72) * (Math.PI / 180);
            const left = 50 + R * Math.cos(angle);
            const top = 50 + R * Math.sin(angle);
            return (
              <div key={label} className={`ibv2-fly__node ${active === idx ? "on" : ""}`}
                style={{ left: `${left}%`, top: `${top}%` }}
                onMouseEnter={() => setActive(idx)} onMouseLeave={() => setActive(null)}
                tabIndex={0} onFocus={() => setActive(idx)} onBlur={() => setActive(null)}>
                <span className="ibv2-fly__pill"><IbIcon icon={icon} size="sm" /></span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
        {/* Mobile vertical — Income flows into the cycle, which loops back to FBS */}
        <div className="mx-auto grid max-w-sm gap-2 md:hidden">
          <div className="flex items-center gap-3 rounded-xl border border-gold/60 bg-white/[0.06] p-3">
            <IbIcon icon={Wallet} size="sm" tone="dark" /><span className="font-serif text-lg text-ivory">Income</span>
          </div>
          <ArrowDown size={16} className="mx-auto text-gold" />
          {stages.map(([icon, label], i) => (
            <div key={label} className="contents">
              <div className="flex items-center gap-3 rounded-xl border border-gold/40 bg-white/5 p-3">
                <IbIcon icon={icon} size="sm" tone="dark" /><span className="font-serif text-lg text-ivory">{label}</span>
              </div>
              {i < stages.length - 1 && <ArrowDown size={16} className="mx-auto text-gold" />}
            </div>
          ))}
          <div className="mt-1 flex items-center justify-center gap-2 rounded-xl border border-gold/50 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-gold"><Repeat size={14} /> Returns to Family Banking System</div>
        </div>
      </Reveal>

      <p className="relative mx-auto mt-12 max-w-3xl text-center text-xs leading-relaxed text-taupe/70">
        Educational framework only. Access, costs, policy values, dividends, guarantees, and loan provisions depend on the carrier, contract, policy design, underwriting, and individual circumstances.
      </p>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 6. PARTICIPATING WHOLE LIFE (6 feature cards)                             */
/* -------------------------------------------------------------------------- */
export function SupportingToolSection() {
  const details = [
    [Coins, "Cash Value", "A contractual value that can build over time."],
    [ShieldCheck, "Death Benefit", "Protection that anchors the whole system."],
    [Banknote, "Policy Loans", "Access to capital using the policy as collateral."],
    [Building2, "Participating Carrier", "A mutual insurer whose contract provisions matter."],
    [CircleDollarSign, "Dividends", "Non-guaranteed and dependent on the carrier."],
    [LockKeyhole, "Underwriting", "Eligibility and design are individual."],
  ];
  return (
    <Section id="supporting-tool" className="bg-[#FBF8F1]">
      <div className="grid gap-12 lg:grid-cols-[.82fr_1.18fr] lg:items-center">
        <Reveal>
          <Eyebrow>The Supporting Tool</Eyebrow>
          <Heading>Participating Whole Life Insurance</Heading>
          <p className="mt-5 font-serif text-2xl italic text-bronze">The policy <span className="accent-word">supports the system.</span> It is not the system.</p>
          <p className="mt-6 text-base leading-relaxed text-slate">
            When appropriate, a properly designed participating whole life policy can provide a contractual foundation for protection, values, and access to capital.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {details.map(([icon, label, sub], i) => (
            <Reveal key={label} i={i}>
              <div className="ibv2-card charcoal-hover flex h-full flex-col items-center gap-2 p-5 text-center">
                <IbIcon icon={icon} />
                <p className="ibv2-card__title mt-1 font-serif text-xl text-legacy">{label}</p>
                <p className="ibv2-card__sub text-xs leading-relaxed text-slate">{sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 7. PROPERLY STRUCTURED (accordion, absorbs compliance)                    */
/* -------------------------------------------------------------------------- */
export function ProperlyStructured() {
  const cards = [
    [Wallet, "Funding", "Premium should support the household's cash flow and stability—not strain it."],
    [Coins, "Cash Value Focus", "Design balances protection, capital access, and contractual limits. Whole life insurance is not an investment and should not be expected to perform like one."],
    [Building2, "Participating Carrier", "Carrier selection and contract provisions matter. Dividends are declared by participating insurers but are not guaranteed."],
    [ShieldCheck, "Policy Design", "Paid-up additions and other design choices require professional, contract-specific guidance. Policy loans accrue interest, and outstanding loans reduce available value and death benefit until repaid."],
    [RefreshCw, "Long-Term Review", "Ongoing attention keeps the system visible. Debt is repaid with real cash flow and a disciplined plan—no strategy makes it disappear."],
  ];
  return (
    <Section id="premium" className="bg-ivory">
      <Reveal className="max-w-3xl">
        <Eyebrow>Properly Structured</Eyebrow>
        <Heading><span className="accent-word">Details matter.</span> The design should serve the purpose.</Heading>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map(([icon, title, copy], i) => (
          <AccordionCard key={title} icon={icon} title={title} i={i}>{copy}</AccordionCard>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 8. FIVE CHARACTERISTICS (accordion, distinct bg)                          */
/* -------------------------------------------------------------------------- */
export function FiveCharacteristics() {
  const cards = [
    [ShieldCheck, "Guarantees", "Guaranteed values are contractual; dividends are non-guaranteed."],
    [Wallet, "Liquidity", "Access is subject to policy value, terms, and loan costs."],
    [CircleDollarSign, "Control", "The owner makes financing and repayment decisions within the contract."],
    [RefreshCw, "Flexibility", "Flexibility is not the same as no responsibility or no cost."],
    [HeartHandshake, "Legacy", "Permanent protection may support legacy planning when appropriate."],
  ];
  return (
    <Section id="characteristics" className="bg-[#EFE8D9]">
      <Reveal className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow">Five Characteristics</p><span className="gold-rule" /></div>
        <Heading>The attributes to understand—without the hype.</Heading>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map(([icon, title, copy], i) => (
          <AccordionCard key={title} icon={icon} title={title} i={i}>{copy}</AccordionCard>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 9. REAL-LIFE USES (bento: 2 featured + 4 supporting)                      */
/* -------------------------------------------------------------------------- */
export function RealLifeUses() {
  const featured = [
    [BriefcaseBusiness, "Business", "Capital access can support timing, inventory, or opportunity in a business."],
    [House, "Real Estate", "Real estate decisions often involve timing, liquidity, and financing choices."],
  ];
  const small = [
    [CarFront, "Vehicle", "Consider how a purchase is financed, not only its price."],
    [GraduationCap, "Education", "Funding education is one more place where method matters."],
    [ShieldCheck, "Emergency", "A ready reserve can reduce reliance on costly outside financing."],
    [Lightbulb, "Opportunity", "When opportunity appears, access can matter as much as the idea."],
  ];
  return (
    <Section id="real-life-uses" className="bg-[#FBF8F1]">
      <Reveal className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow">Real Life Uses</p><span className="gold-rule" /></div>
        <Heading><span className="accent-word">Capital</span> can be directed toward life—not a generic spreadsheet.</Heading>
        <p className="mt-5 text-slate">Examples only. No use is automatically suitable, and no outcome is promised.</p>
      </Reveal>
      <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {featured.map(([icon, label, desc], i) => (
          <Reveal key={label} i={i} className="lg:col-span-2">
            <div className="ibv2-card charcoal-hover group flex h-full flex-col justify-between overflow-hidden p-7 md:min-h-[220px]">
              <IbIcon icon={icon} size="lg" />
              <div className="mt-6">
                <p className="ibv2-card__title font-serif text-3xl text-legacy">{label}</p>
                <p className="ibv2-card__sub mt-2 max-w-md text-sm leading-relaxed text-slate">{desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
        {small.map(([icon, label, desc], i) => (
          <Reveal key={label} i={i}>
            <div className="ibv2-card charcoal-hover group flex h-full flex-col p-5">
              <IbIcon icon={icon} />
              <p className="ibv2-card__title mt-4 font-serif text-2xl text-legacy">{label}</p>
              <p className="ibv2-card__sub mt-2 max-h-0 overflow-hidden text-sm leading-relaxed text-slate opacity-0 transition-all duration-500 group-hover:max-h-24 group-hover:opacity-100 group-focus-within:max-h-24 group-focus-within:opacity-100">{desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 10. FINANCING CHOICES (3 capital paths)                                   */
/* -------------------------------------------------------------------------- */
export function FinancingComparison() {
  const choices = [
    { title: "Pay Cash", icon: Wallet, tone: "gray", steps: ["Capital leaves your account", "Purchase is complete", "Liquidity is reduced", "No repayment, but capital is gone"] },
    { title: "Borrow Traditionally", icon: Building2, tone: "dark", steps: ["A lender provides capital", "Purchase is complete", "Payments + interest go outward", "Interest leaves your system"] },
    { title: "Family Banking", icon: Landmark, tone: "gold", steps: ["Capital is accessed", "Purchase is complete", "Repayment returns to your system", "Capital is available to reuse"] },
  ];
  const toneStyles = {
    gray: { card: "border-taupe/50 bg-white", rail: "#9a948b", label: "text-slate" },
    dark: { card: "border-legacy/25 bg-[#F3F0EA]", rail: "#4a4640", label: "text-legacy" },
    gold: { card: "border-gold/70 bg-[linear-gradient(165deg,#fff,#fbf2df)] shadow-[0_26px_55px_-34px_rgba(17,17,17,0.45)]", rail: "url(#ibv2-gold)", label: "text-bronze" },
  };
  return (
    <Section id="financing-choices" className="bg-ivory">
      <Reveal className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-4"><span className="gold-rule" /><p className="eyebrow">Financing Choices</p><span className="gold-rule" /></div>
        <Heading>Every purchase has a capital path.</Heading>
      </Reveal>
      <div className="mt-10 grid items-stretch gap-5 lg:grid-cols-3">
        {choices.map(({ title, icon, tone, steps }, ci) => {
          const s = toneStyles[tone];
          return (
            <Reveal key={title} i={ci}>
              <article className={`ibv2-card group flex h-full flex-col rounded-[20px] border p-7 ${s.card}`}>
                <div className="flex items-center justify-center gap-3 text-center">
                  <IbIcon icon={icon} tone={tone === "gold" ? "dark" : ""} />
                  <h3 className="font-serif text-2xl text-legacy">{title}</h3>
                </div>
                <ol className="mt-6 flex flex-1 flex-col">
                  {steps.map((step, i) => (
                    <li key={step} className="flex flex-col">
                      <span className={`flex min-h-[3.75rem] items-center justify-center rounded-xl border px-5 py-2.5 text-center text-sm leading-snug transition-colors duration-300 ${tone === "gold" ? "border-gold/45 bg-white text-legacy group-hover:border-gold group-hover:bg-[#fffaf0]" : "border-taupe/40 bg-white text-slate group-hover:border-gold/50"}`}>{step}</span>
                      {i < steps.length - 1 && (
                        <svg width="14" height="22" viewBox="0 0 14 22" className="mx-auto my-1" aria-hidden="true">
                          {goldDefs()}
                          <line x1="7" y1="0" x2="7" y2="15" stroke={s.rail} strokeWidth="2.6" className={tone === "gold" ? "ibv2-flow-dash" : ""} />
                          <path d="M2 13 L7 20 L12 13" fill="none" stroke={s.rail} strokeWidth="2.6" />
                        </svg>
                      )}
                    </li>
                  ))}
                </ol>
              </article>
            </Reveal>
          );
        })}
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-[11px] leading-relaxed text-slate/60">
        There is no universally best path. This comparison is educational and does not replace individual financial, insurance, legal, or tax guidance.
      </p>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 11. COACHING & IMPLEMENTATION (IB-specific timeline)                      */
/* -------------------------------------------------------------------------- */
export function CoachingTimeline() {
  const steps = [
    ["Learn", "Understand the problem, principles, and language before making major decisions."],
    ["Design", "Organize the current financial picture and identify where control, liquidity, and protection need attention."],
    ["Implement", "Put appropriate tools and strategies in place only after their purpose is understood."],
    ["Track", "Keep the system visible and measurable as policies, loans, repayments, and goals evolve."],
    ["Improve", "Review progress, adapt to changes, and continue expanding the system over time."],
  ];
  return (
    <Section id="process" className="bg-[#EFE8D9]">
      <Reveal className="max-w-3xl">
        <Eyebrow>The Coaching Experience</Eyebrow>
        <Heading>A <span className="accent-word">financial system</span> is built, implemented, and improved over time.</Heading>
        <p className="mt-5 text-base leading-relaxed text-slate md:text-lg">The value is not simply choosing financial tools. It is understanding why each decision is made, where it fits, and how to keep the system moving in the right direction.</p>
      </Reveal>
      <ol className="relative mt-16 grid gap-8 lg:grid-cols-5 lg:gap-6">
        <span aria-hidden="true" className="absolute left-6 right-6 top-6 hidden h-px bg-gradient-to-r from-gold/25 via-gold/70 to-gold/25 lg:block" />
        {steps.map(([title, copy], index) => (
          <Reveal key={title} i={index} as="li">
            <div className="ibv2-step group relative flex gap-4 lg:flex-col lg:gap-0">
              {index < steps.length - 1 && (
                <span aria-hidden="true" className="absolute left-6 top-14 h-[calc(100%+2rem-3.5rem)] w-px bg-gold/40 lg:hidden" />
              )}
              <span className="ibv2-step__num relative z-10">{index + 1}</span>
              <div className="lg:mt-6">
                <h3 className="font-serif text-xl leading-tight text-legacy lg:text-2xl">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">{copy}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </ol>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 12. POLICY STACK (IB-specific, mirrored, framed)                          */
/* -------------------------------------------------------------------------- */
const policyValues = [
  "One organized household view",
  "Policy information in one place",
  "Loan and repayment tracking",
  "Better preparation for coaching reviews",
  "Ongoing education and engagement",
];

export function IbPolicyStack() {
  return (
    <Section id="policy-stack" className="relative overflow-hidden bg-ivory">
      <div className="pointer-events-none absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_18%_20%,rgba(197,161,90,0.16),transparent_28%)]" />
      <div className="relative grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
        {/* Screenshot first (mirrored vs supporting-tool which had copy-left) */}
        <Reveal>
          <div className="ibv2-frame">
            <div className="overflow-hidden rounded-2xl border border-gold/30 bg-white">
              <img src={policyStackImg} alt="Policy Stack Infinite Banking Concept Dashboard" loading="lazy" className="block h-auto w-full object-contain" />
            </div>
          </div>
        </Reveal>
        <Reveal i={1}>
          <div className="flex items-center gap-4"><span className="gold-rule" /><p className="eyebrow">Where Education Becomes Implementation</p></div>
          <Heading>Where your financial system <span className="accent-word">comes to life.</span></Heading>
          <p className="mt-6 text-base leading-relaxed text-slate">A financial strategy should not disappear into a folder after implementation. Policy Stack gives each household a central place to organize policies, monitor important values, and track loans and repayments over time.</p>
          <div className="mt-6 rounded-2xl border border-gold/40 bg-white p-5 shadow-[0_18px_40px_-30px_rgba(17,17,17,0.35)]">
            <p className="font-serif text-xl text-legacy">Greater visibility into your Family Banking System.</p>
            <ul className="mt-4 grid gap-2.5">
              {policyValues.map((v) => (
                <li key={v} className="flex items-start gap-2 text-sm leading-relaxed text-slate"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-bronze" />{v === "Ongoing education and engagement" ? <span>Ongoing education and<br />engagement</span> : v}</li>
              ))}
            </ul>
          </div>
          <a href="https://policystack.co/?ref=LEGACYLIFE&via=legacylife-general" target="_blank" rel="noopener noreferrer" aria-label="Explore Policy Stack (opens the Policy Stack website in a new tab)" data-testid="policy-stack-cta" className="btn-base btn-outline-dark mt-8">Explore Policy Stack <ArrowRight size={17} /></a>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 13. CONTINUE LEARNING (video shelves)                                     */
/* -------------------------------------------------------------------------- */
export function VideoShelf() {
  const groups = [
    ["Getting Started", [["sVLJdJgDnTA", "Short Infinite Banking Explainer"], ["HxzJZ6liVrI", "A Beginner's Guide to Becoming Your Own Banker"], ["bLrNDKqGwfE", "You Don't Need to Make More Money | You Need to Keep More Money"]]],
    ["Policy Design", [["NpBrfndYNKU", "How Much Premium Should You Pay?"], ["MOrFy2QqBHE", "Overfunding or Maximizing the Policy"], ["y_VdAZ3FgsU", "The Honest Truth About Term Versus Whole Life Cost"]]],
    ["Applications", [["UCXJC9vLlOI", "How to Pay Off Debt With Infinite Banking"], ["M_qoZp92BCc", "Banking for Children"], ["7j0dY4vOqcw", "Should You Replace Your Bond Portfolio With Whole Life?"]]],
    ["Common Mistakes", [["mthNIN4myd0", "Avoid These Mistakes and You'll Be a Successful Banker"], ["zgOo5cgt-mY", "The Most Important Advice You'll Hear When Starting IBC"]]],
  ];
  return (
    <Section id="continue-learning" className="bg-[#FBF8F1]">
      <span id="explainer" className="block scroll-mt-24" />
      <Reveal className="max-w-3xl">
        <Eyebrow>Continue Learning</Eyebrow>
        <Heading>Explore the ideas at your own pace.</Heading>
      </Reveal>
      <div className="mt-10 space-y-12">
        {groups.map(([heading, videos]) => (
          <div key={heading}>
            <div className="mb-5 flex items-center gap-3">
              <h3 className="font-serif text-2xl text-legacy">{heading}</h3>
              <span className="h-px flex-1 bg-gold/25" />
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {videos.map(([id, title], i) => (
                <Reveal key={id} i={i}><VideoBlock id={id} title={title} className="ibv2-video" /></Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 14. SAVE & SPEND OVERVIEW + NEXT STEP (combined two-card)                  */
/* -------------------------------------------------------------------------- */
export function SaveSpendNextStep() {
  return (
    <Section id="overview" className="bg-[#EFE8D9]">
      <div className="grid items-stretch gap-6 lg:grid-cols-2">
        {/* LEFT — gated overview form (light) */}
        <Reveal>
          <div id="cta-overview" className="flex h-full flex-col rounded-[22px] border border-gold/40 bg-white p-7 shadow-[0_24px_55px_-38px_rgba(17,17,17,0.4)] md:p-9">
            <p className="eyebrow text-bronze">Save &amp; Spend Overview</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-[1.1] text-legacy md:text-4xl">Ready for the complete overview?</h2>
            <p className="mt-4 text-[.95rem] leading-relaxed text-slate">Access the full Save &amp; Spend System presentation for a deeper explanation of the Infinite Banking process.</p>
            <div className="mt-6 text-left">
              <GatedForm list="Save & Spend System Overview" source="infinite-banking" note="Gated video. A secure access link is revealed after signup." />
            </div>
          </div>
        </Reveal>
        {/* RIGHT — next step (charcoal) */}
        <Reveal i={1}>
          <div id="cta" className="relative flex h-full flex-col overflow-hidden rounded-[22px] bg-legacy p-8 text-ivory md:p-10">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.28),transparent_70%)]" />
            <p className="eyebrow relative text-gold">Build With Intention</p>
            <h2 className="relative mt-4 font-serif text-3xl font-semibold leading-[1.08] tracking-display text-ivory md:text-4xl lg:text-[2.75rem]">Build the system before choosing the tools.</h2>
            <p className="relative mt-5 max-w-md text-[.95rem] leading-relaxed text-taupe">Start with a straightforward conversation about your cash flow, priorities, existing coverage, and what you want your financial system to accomplish.</p>
            <div className="relative mt-auto flex flex-col gap-4 pt-8 sm:flex-row">
              <Link to="/strategy-call" data-testid="ib-final-cta" className="btn-base btn-gold"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
              <Link to="/policy-review" className="btn-base btn-outline-light">Request a Policy Review <ArrowRight size={18} /></Link>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/* 15. MASTERING MONEY MONDAYS — closing band above footer (charcoal)        */
/* -------------------------------------------------------------------------- */
export function MasteringMoneyClosing() {
  return (
    <section id="newsletter" data-testid="mastering-money-section" className="relative overflow-hidden border-b border-bronze/15 bg-[linear-gradient(180deg,#EFE7D6,#E4D9C4)] py-16 md:py-20">
      <div className="pointer-events-none absolute -right-16 top-0 h-52 w-52 rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.25),transparent_70%)]" />
      <div className="container-lr">
        <Reveal className="grid items-center gap-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-legacy text-gold"><Mail size={20} strokeWidth={1.6} /></span>
              <p className="eyebrow text-bronze">Our Weekly Newsletter</p>
            </div>
            <h2 className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-display text-legacy md:text-5xl">Mastering Money <span className="accent-word">Mondays</span></h2>
            <p className="mt-3 font-serif text-xl italic leading-snug text-bronze md:text-2xl">Build a better financial system. One Monday at a time.</p>
          </div>
          <div className="ibv2-newsletter lg:border-l lg:border-bronze/20 lg:pl-8">
            <LeadForm
              list="Weekly newsletter sign ups"
              source="resources"
              layout="inline"
              submitLabel="Join"
              showDisclosure={false}
              note="A weekly newsletter — not a gated download. Unsubscribe anytime."
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
